import { Component, Inject, Prop, Ref, Vue, Watch } from 'vue-property-decorator';
import _ from 'lodash';
import { IApplicationCenter } from '@/shared/model/travelpurpose/application-center.model';
import ApplicationCenterService from '@/visa-application/services/travelpurpose/application-center/application-center.service';
import { IVisaSubtype } from '@/shared/model/travelpurpose/visa-subtype.model';
import TravelPurposeThemeCMSModel from '@/visa-application/models/TravelPurposeThemeCMSModel';
import DirectusService from '@/core/directus/directus.service';
import LanguageCode from '@/locale/language-code';
import VisaTypeCMSModel from '@/visa-application/models/VisaTypeCMSModel';
import { INDI_TYPE } from '@/constants';
import TravelPurposeThemeDisplayOrderCMSModel from '@/visa-application/models/TravelPurposeThemeDisplayOrderCMSModel';
import { getVisatypeDocumentUrl } from '@/shared/utils/utils';

export interface TravelPurposeVisaTypeInterface {
  travelPurpose: TravelPurposeThemeCMSModel;
  visaTypes: VisaTypeCMSModel[];
}

@Component
export default class VisaSubtypeSelection extends Vue {
  @Ref() readonly carouselContainer!: HTMLElement;
  @Ref() readonly carouselItems!: HTMLElement;

  @Inject('applicationCenterService')
  private applicationCenterService: () => ApplicationCenterService;
  @Inject('directusService')
  private directusService: () => DirectusService;

  @Prop() private issuer: string;

  @Watch('$store.getters.currentLanguage', { deep: true })
  public onLanguageChange(language: LanguageCode) {
    this.currentLanguage = language;
    this.getTravelPurposeThemeContent();
  }
  @Watch('travelPurposeDisplay', { deep: true })
  public onTravelPurposeDisplayChange() {
    this.updateDimensions();
  }

  public applicationCenter: IApplicationCenter = {};
  public travelPurposeThemeCodes: string[] = [];
  public travelPurposeThemesFromCMS: TravelPurposeThemeCMSModel[] = [];
  public travelPurposeDisplay: TravelPurposeThemeCMSModel[] = [];
  public travelPurposeAll: TravelPurposeThemeCMSModel[] = [];
  public visaTypesFromCMS: VisaTypeCMSModel[] = [];
  public cmsData: TravelPurposeVisaTypeInterface[] = [];
  public travelPurposeThemeSelected?: TravelPurposeThemeCMSModel = null;
  public visaSubtypes: IVisaSubtype[];
  public currentLanguage: LanguageCode = this.$store.getters.currentLanguage;
  public currentIndex = -1;
  public itemWidth = 180;
  public visibleItems = Math.floor(window.innerWidth / this.itemWidth);
  public maxWidthTransform = 0;
  public centralizeCarousel = false;

  get carouselItemsTransform() {
    return Math.min(this.currentIndex * this.itemWidth, this.maxWidthTransform);
  }
  get showPreviousButton() {
    return this.currentIndex !== -1 && this.currentIndex !== 0;
  }
  get showNextButton() {
    return this.travelPurposeDisplay.length > 1 && this.currentIndex !== this.travelPurposeDisplay.length - 1;
  }

  public retrieveApplicationCenterByIssuer(issuer) {
    this.applicationCenterService()
      .getIssuerAndVisatypeInfoByIssuerAndAccountType(issuer, INDI_TYPE)
      .then(res => {
        this.applicationCenter = res;
        this.retrieveTravelPurposeThemeCodes();
      })
      .catch(error => console.error(error));
  }

  public retrieveTravelPurposeThemeCodes() {
    this.travelPurposeThemeCodes = _.union(
      _.flatMap(this.applicationCenter.visaSubtypes, visaSubtype => visaSubtype.travelPurposeThemeCode)
    );

    this.getTravelPurposeThemeContent();
  }

  public selectTheme(index: number): void {
    this.currentIndex = index;

    this.travelPurposeThemeSelected = this.travelPurposeDisplay[index];
    this.retrieveVisaSubtypesFromTheme();
  }

  public getVisaSubtypeDocumentUrl(visaSubtype: VisaTypeCMSModel): string {
    return getVisatypeDocumentUrl(visaSubtype);
  }

  public getTravelPurposeThemeContent(): void {
    const filter = {
      code: {
        in: this.travelPurposeThemeCodes
      }
    };

    this.directusService()
      .getCollection<TravelPurposeThemeCMSModel>(TravelPurposeThemeCMSModel, 'travel_purpose_themes', ['*.*'], filter, this.currentLanguage)
      .then((res: TravelPurposeThemeCMSModel[]) => {
        this.travelPurposeThemesFromCMS = res;

        const filter2 = {
          'vac.code': {
            eq: this.issuer
          }
        };
        this.directusService()
          .getCollection<TravelPurposeThemeDisplayOrderCMSModel>(
            TravelPurposeThemeDisplayOrderCMSModel,
            'travel_purpose_theme_display_order',
            ['*.*'],
            filter2,
            this.currentLanguage
          )
          .then((travelPurposeThemeDisplayOrders: TravelPurposeThemeDisplayOrderCMSModel[]) => {
            travelPurposeThemeDisplayOrders.forEach(travelPurposeThemeDisplayOrder => {
              this.travelPurposeThemesFromCMS.forEach(travelPurposeTheme => {
                if (travelPurposeTheme.code === travelPurposeThemeDisplayOrder.travel_purpose_theme.code) {
                  if (_.get(travelPurposeThemeDisplayOrder, 'weight')) {
                    travelPurposeTheme.weight = travelPurposeThemeDisplayOrder.weight;
                  }
                  if (_.get(travelPurposeThemeDisplayOrder, 'description.length') > 0) {
                    travelPurposeTheme.description = travelPurposeThemeDisplayOrder.description;
                  }
                  if (_.get(travelPurposeThemeDisplayOrder, 'title.length') > 0) {
                    travelPurposeTheme.title = travelPurposeThemeDisplayOrder.title;
                  }
                }
              });
            });
            this.getVisaTypesContent();
          })
          .catch(err => console.error(err));

        this.getVisaTypesContent();
      })
      .catch(err => console.error(err));
  }

  public getVisaTypesContent(): void {
    if (this.issuer === '') {
      return;
    }
    const filter = {
      'application_center.code': {
        eq: this.issuer
      },
      account_type: {
        eq: INDI_TYPE
      }
    };

    this.directusService()
      .getCollection<VisaTypeCMSModel>(VisaTypeCMSModel, 'visa_types', ['*.*.*', 'visa_types_content.*.*.*'], filter, this.currentLanguage)
      .then((res: VisaTypeCMSModel[]) => {
        this.visaTypesFromCMS = res.filter(el => el.is_active);
        this.mapDataFromCMS();
      })
      .catch(error => console.error(error));
  }

  public mapDataFromCMS(): void {
    const cmsModelMap: TravelPurposeVisaTypeInterface[] = [];

    _.forEach(this.travelPurposeThemeCodes, travelPurposeCode => {
      const travelPurposeCMS: TravelPurposeThemeCMSModel = _.find(
        this.travelPurposeThemesFromCMS,
        travelPurpose => travelPurpose.code === travelPurposeCode
      );

      if (travelPurposeCMS) {
        const visaSubtypes: IVisaSubtype[] = _.filter(
          this.applicationCenter.visaSubtypes,
          visaSubtype => visaSubtype.travelPurposeThemeCode === travelPurposeCode
        );

        cmsModelMap.push({
          travelPurpose: travelPurposeCMS,
          visaTypes: _.filter(this.visaTypesFromCMS, visaType => _.find(visaSubtypes, { code: visaType.code, stay: visaType.stay }))
        });
      }
    });

    this.cmsData = cmsModelMap;
    this.travelPurposeAll = _.flatMap(this.cmsData, 'travelPurpose');
    this.travelPurposeDisplay = _.sortBy(this.travelPurposeAll, 'weight').reverse();
  }

  private retrieveVisaSubtypesFromTheme(): void {
    this.visaSubtypes = this.applicationCenter.visaSubtypes.filter(
      visaSubtype => visaSubtype.travelPurposeThemeCode === this.travelPurposeThemeSelected.code
    );
  }

  public selectNextTheme(): void {
    this.currentIndex = Math.min(this.currentIndex + 1, this.travelPurposeDisplay.length - 1);

    this.selectTheme(this.currentIndex);

    this.retrieveVisaSubtypesFromTheme();
  }

  public selectPreviousTheme(): void {
    this.currentIndex = Math.max(this.currentIndex - 1, 0);

    this.selectTheme(this.currentIndex);

    this.retrieveVisaSubtypesFromTheme();
  }

  public getShowVisaTypeText(visaType: VisaTypeCMSModel): string {
    if (_.get(visaType, 'title')) {
      return _.get(visaType, 'title');
    } else {
      return _.get(visaType, 'stay', '') + ' ' + _.get(visaType, 'code', '');
    }
  }

  private updateDimensions() {
    const containerWidth = this.carouselContainer.offsetWidth;
    const itemsWidth = this.carouselItems.offsetWidth;

    this.visibleItems = Math.floor(containerWidth / this.itemWidth);
    this.maxWidthTransform = itemsWidth - containerWidth;
    this.centralizeCarousel = itemsWidth === 0 ? false : this.maxWidthTransform < 0;
    this.itemWidth = this.$refs['carousel-item-0'] ? this.$refs['carousel-item-0'][0].offsetWidth : this.itemWidth;
  }

  public created(): void {
    this.retrieveApplicationCenterByIssuer(this.issuer);
  }

  public mounted(): void {
    window.addEventListener('resize', this.updateDimensions);
  }

  public beforeDestroy(): void {
    window.removeEventListener('resize', this.updateDimensions);
  }
}
